import _ from 'lodash';
import { T } from 'src/shared/helpers';
import theme from 'src/shared/theme';

export const MAX_RESIZABLE_AREA_IN_PX = 768;

export const units = [
    { value: 'm', label: T._('m', 'Admin'), ratio: 100 },
    { value: 'cm', label: T._('cm', 'Admin'), ratio: 1 },
    { value: 'mm', label: T._('mm', 'Admin'), ratio: 0.1 },
    { value: 'in', label: T._('in', 'Admin'), ratio: 2.54 }
] as const;

export type Unit = (typeof units)[number];

export type Tool = 'none' | 'ruler' | 'pan' | 'rectangle' | 'circle' | 'polygon';

export interface AreaTemplateInfo {
    borderSvg: string;
    sizeCm: { width: number; height: number; isEmpty: boolean };
    sizePt: { width: number; height: number; isEmpty: boolean };
    layers: { name: string; svg: string }[];
}

export const testAreaTemplateInfo = {
    sizePt: {
        isEmpty: false,
        width: 595.28,
        height: 841.89
    },
    sizeCm: {
        isEmpty: false,
        width: 21.000156,
        height: 29.700008
    },
    borderSvg:
        '<?xml version="1.0" encoding="UTF-8"?><svg xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink" width="595.28pt" height="841.89pt" viewBox="0 0 595.28 841.89" version="1.2"><path  d="M 0.0 0.0 L 595.28 0.0 L 595.28 841.89 L 0.0 841.89 L 0.0 0.0 Z" fill="none" stroke="none"/></svg>',
    layers: [
        {
            name: 'Livello 1',
            svg: '<?xml version="1.0" encoding="UTF-8"?>\n<svg xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink" width="595.28pt" height="841.89pt" viewBox="0 0 595.28 841.89" version="1.2">\n<g id="surface1">\n<path style="fill:none;stroke-width:5;stroke-linecap:butt;stroke-linejoin:miter;stroke:rgb(0%,67.83905%,93.728638%);stroke-opacity:1;stroke-miterlimit:10;" d="M 488.308653 243.94546 L 106.972979 243.94546 L 106.972979 597.943421 L 488.308653 597.943421 Z M 488.308653 243.94546 " transform="matrix(0.998792,0,0,-0.998792,-0.000000000000016209,841.326542)"/>\n</g>\n</svg>\n'
        },
        {
            name: 'Livello 2',
            svg: '<?xml version="1.0" encoding="UTF-8"?>\n<svg xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink" width="595.28pt" height="841.89pt" viewBox="0 0 595.28 841.89" version="1.2">\n<g id="surface1">\n<path style="fill:none;stroke-width:5;stroke-linecap:butt;stroke-linejoin:miter;stroke:rgb(100%,94.898987%,0%);stroke-opacity:1;stroke-miterlimit:10;" d="M 0.000867709 0.0000222316 L -93.080329 33.548363 L -173.482147 -24.111137 L -170.341634 74.777858 L -250.019921 133.430745 L -154.99888 160.999205 L -123.844056 254.905618 L -68.261284 173.05674 L 30.678553 172.442717 L -29.988486 94.2858 Z M 0.000867709 0.0000222316 " transform="matrix(0.998792,0,0,-0.998792,406.819446,536.14846)"/>\n</g>\n</svg>\n'
        }
    ]
};

export const getItemChildrens = (parent: paper.Item) => {
    const childrens: paper.Item[] = [];

    const getChildrens = (parent: paper.Item) => {
        childrens.push(parent);

        if (parent.children) {
            for (const children of parent.children) {
                getChildrens(children);
            }
        }
    };

    getChildrens(parent);
    return childrens;
};

export const getItemParents = (item: paper.Item) => {
    const parents: paper.Item[] = [];

    const getParent = (item: paper.Item) => {
        parents.push(item);

        if (item.parent) {
            getParent(item.parent);
        }
    };

    getParent(item);
    return parents;
};

export const snapDeltaToAngle = (delta: paper.Point, snapAngle: number) => {
    const snap = (180 / Math.PI) * snapAngle;

    let angle = delta.angle;
    angle = Math.round(angle / snap) * snap;

    const newDelta = delta.clone();
    newDelta.angle = angle;

    return newDelta;
};

export const getContrastYIQ = (r: number, g: number, b: number) => {
    var yiq = (r * 299 + g * 587 + b * 114) / 1000;
    return yiq >= 128;
};

export const getContrastYIQHex = (hexcolor: string) => {
    var r = parseInt(hexcolor.substr(0, 2), 16);
    var g = parseInt(hexcolor.substr(2, 2), 16);
    var b = parseInt(hexcolor.substr(4, 2), 16);
    var yiq = (r * 299 + g * 587 + b * 114) / 1000;
    return yiq >= 128;
};

export const getContrast = (rect: paper.Rectangle, sideImage: paper.Raster) => {
    let isLight = false;

    if (sideImage) {
        rect.height = Math.max(1, rect.height);
        rect.width = Math.max(1, rect.width);

        try {
            var avgColor = sideImage.getAverageColor(rect);

            if (avgColor) {
                isLight = getContrastYIQ(avgColor.red * 255, avgColor.green * 255, avgColor.blue * 255);
            }
        } catch (e: any) {
            console.error('error getting image avarage color ' + e.message);
        }
    }
    return isLight;
};

export const createRulerLine = (paper: paper.PaperScope, sideImage: paper.Raster, from: paper.Point, to: paper.Point) => {
    const measureMarkers: paper.Item[] = [];

    let line = new paper.Path.Line(new paper.Point(0, 0), new paper.Point(0, 0));
    measureMarkers.push(line);

    line.strokeColor = getContrast(line.bounds, sideImage) ? new paper.Color('black') : new paper.Color('white');
    const color = line.strokeColor;

    line.strokeWidth = 1.0 / paper.view.zoom;
    line.dashOffset = 1 / paper.view.zoom;
    line.dashArray = [1.0 / paper.view.zoom, 1.0 / paper.view.zoom];

    // Arrows and end lines
    line = new paper.Path.Line(new paper.Point(0, 0), new paper.Point(0, 0));
    line.strokeColor = color;
    line.strokeWidth = 1.0 / paper.view.zoom;
    line.dashOffset = 1 / paper.view.zoom;
    line.dashArray = [1.0 / paper.view.zoom, 1.0 / paper.view.zoom];

    measureMarkers.push(line);

    line = new paper.Path.Line(new paper.Point(0, 0), new paper.Point(0, 0));
    line.strokeColor = color;
    line.strokeWidth = 1.0 / paper.view.zoom;
    line.dashOffset = 1 / paper.view.zoom;
    line.dashArray = [1.0 / paper.view.zoom, 1.0 / paper.view.zoom];

    measureMarkers.push(line);

    const t1 = new paper.Path();
    t1.add(new paper.Point(0, 0));
    t1.add(new paper.Point(0, 0));
    t1.add(new paper.Point(0, 0));
    t1.closed = true;
    t1.strokeColor = color;
    t1.fillColor = color;
    measureMarkers.push(t1);

    const t2 = new paper.Path();
    t2.add(new paper.Point(0, 0));
    t2.add(new paper.Point(0, 0));
    t2.add(new paper.Point(0, 0));
    t2.closed = true;
    t2.strokeColor = color;
    t2.fillColor = color;

    measureMarkers.push(t2);

    // Measure tag
    const measureTag = new paper.Group();
    measureTag.applyMatrix = false;

    const measureTagRect = new paper.Path.Rectangle(new paper.Rectangle(0, 0, 70, 24), new paper.Size(4, 4));
    measureTagRect.fillColor = new paper.Color('#ffffff');
    measureTagRect.strokeScaling = false;
    measureTagRect.strokeWidth = 0.5;
    measureTagRect.strokeColor = new paper.Color('#9ca3af');

    const measureTagText = new paper.PointText(new paper.Point(35, 16));
    measureTagText.content = '100 cm';
    measureTagText.fontSize = 11;
    measureTagText.fontWeight = 400;
    measureTagText.fontFamily = 'Inter, Montserrat, sans-serif';
    measureTagText.fillColor = new paper.Color('#455a64');
    measureTagText.justification = 'center';

    measureTag.addChildren([measureTagRect, measureTagText]);

    measureMarkers.push(measureTag, measureTagRect, measureTagText);

    updateRulerLine(paper, measureMarkers, sideImage, 0, units[0], from, to);

    return measureMarkers;
};

export const updateRulerLine = (
    paper: paper.PaperScope,
    measureMarkers: paper.Item[],
    sideImage: paper.Raster,
    ppcm: number,
    unit: Unit,
    from: paper.Point,
    to: paper.Point
) => {
    let startPoint = from.clone();
    let direction: 1 | 2 = 1;

    let vector = to.subtract(from);
    vector = snapDeltaToAngle(vector, Math.PI / 2);

    let endPoint = from.add(vector);

    if (Math.abs(startPoint.x - endPoint.x) < 0.0000001) endPoint.x = startPoint.x;

    if (Math.abs(startPoint.y - endPoint.y) < 0.0000001) endPoint.y = startPoint.y;

    if (startPoint.x == endPoint.x && startPoint.y == endPoint.y) return;

    if (startPoint.x > endPoint.x || startPoint.y > endPoint.y) {
        const temp = startPoint;
        startPoint = endPoint;
        endPoint = temp;
    }

    (measureMarkers[0] as paper.Path).firstSegment.point = startPoint;
    (measureMarkers[0] as paper.Path).lastSegment.point = endPoint;

    // Create markers
    const d = 8 / paper.view.zoom;
    let p1 = new paper.Point(0, 0);
    let p2 = new paper.Point(0, 0);
    let p3 = new paper.Point(0, 0);
    let p4 = new paper.Point(0, 0);
    let pt1 = new paper.Point(0, 0);
    let pt2 = new paper.Point(0, 0);
    let pt3 = new paper.Point(0, 0);
    let pt4 = new paper.Point(0, 0);

    vector = new paper.Point(0, 0);
    vector.length = 6 / paper.view.zoom;

    if (Math.abs(startPoint.x - endPoint.x) > 0.0000001) {
        direction = 1;

        p1 = new paper.Point(startPoint.x, startPoint.y - d);
        p2 = new paper.Point(startPoint.x, startPoint.y + d);
        p3 = new paper.Point(endPoint.x, endPoint.y - d);
        p4 = new paper.Point(endPoint.x, endPoint.y + d);

        vector.angle = -30;
        pt1 = startPoint.add(vector);
        vector.angle = 30;
        pt2 = startPoint.add(vector);

        vector.angle = -150;
        pt3 = endPoint.add(vector);
        vector.angle = 150;
        pt4 = endPoint.add(vector);
    } else if (Math.abs(startPoint.y - endPoint.y) > 0.0000001) {
        direction = 2;

        p1 = new paper.Point(startPoint.x - d, startPoint.y);
        p2 = new paper.Point(startPoint.x + d, startPoint.y);
        p3 = new paper.Point(endPoint.x - d, endPoint.y);
        p4 = new paper.Point(endPoint.x + d, endPoint.y);

        vector.angle = 120;
        pt1 = startPoint.add(vector);
        vector.angle = 60;
        pt2 = startPoint.add(vector);

        vector.angle = -120;
        pt3 = endPoint.add(vector);
        vector.angle = -60;
        pt4 = endPoint.add(vector);
    }

    //line.strokeColor = color;
    measureMarkers[1].strokeWidth = 1.0 / paper.view.zoom;
    measureMarkers[1].dashOffset = 1 / paper.view.zoom;
    measureMarkers[1].dashArray = [1.0 / paper.view.zoom, 1.0 / paper.view.zoom];

    //line.strokeColor = color;
    measureMarkers[2].strokeWidth = 1.0 / paper.view.zoom;
    measureMarkers[2].dashOffset = 1 / paper.view.zoom;
    measureMarkers[2].dashArray = [1.0 / paper.view.zoom, 1.0 / paper.view.zoom];

    (measureMarkers[3] as paper.Path).segments[0].point = startPoint;
    (measureMarkers[3] as paper.Path).segments[1].point = pt1;
    (measureMarkers[3] as paper.Path).segments[2].point = pt2;
    (measureMarkers[3] as paper.Path).closed = true;
    // t1.strokeColor = color;
    // t1.fillColor = color;

    (measureMarkers[4] as paper.Path).segments[0].point = endPoint;
    (measureMarkers[4] as paper.Path).segments[1].point = pt3;
    (measureMarkers[4] as paper.Path).segments[2].point = pt4;
    (measureMarkers[4] as paper.Path).closed = true;
    // t2.strokeColor = color;
    // t2.fillColor = color;

    const scale = 1 / paper.view.zoom;
    measureMarkers[5].scaling = new paper.Point(scale, scale);
    measureMarkers[5].position =
        direction === 1 ? endPoint.add(new paper.Point(0, -40 / paper.view.zoom)) : endPoint.add(new paper.Point(-60 / paper.view.zoom, 0));
    measureMarkers[5].visible = ppcm > 0;

    if (ppcm > 0) {
        (measureMarkers[7] as paper.PointText).content = _.round((measureMarkers[0] as paper.Path).length / ppcm / unit.ratio, 3).toFixed(3) + ' ' + unit.value;
    }

    // Scale the label rectangle to fit the label size
    const tagMinWidth = 1 / paper.view.zoom;
    const scaleRatio = Math.max(tagMinWidth, measureMarkers[7].bounds.width + 20) / measureMarkers[6].bounds.width;
    measureMarkers[6].scale(scaleRatio, 1);
};

interface AreaDesignerSettings {
    unit: (typeof units)[number]['value'];
}

export const getStartingUnit = () => {
    let unit: Unit = units[1];

    try {
        const settings: AreaDesignerSettings | null = localStorage.getItem('area_designer_settings')
            ? JSON.parse(localStorage.getItem('area_designer_settings')!)
            : null;
        if (settings) {
            const newUnit = units.find((x) => x.value === settings.unit);

            if (newUnit) unit = newUnit;
        }
    } catch {}

    return unit;
};
